import ApiService from '@/core/services/api.service';

const api = new ApiService();

// API trả về bọc { meta, data, error }. Một vài endpoint trả thẳng payload,
// nên bóc mềm để chỗ gọi không phải xử lý hai kiểu.
function unwrap(res) {
  if (res === null || res === undefined) return null;
  if (Object.prototype.hasOwnProperty.call(res, 'data')) return res.data;
  return res;
}

function throwIfError(res) {
  if (res && res.error && res.error.message) {
    throw res.error;
  }
  return res;
}

// Enum bên BE là CO/CHU/ANH/CHI/EM/MOINGUOI (không dấu), map sang nhãn tiếng Việt.
//
// "Mọi người" CHỈ có ở đây, không đưa vào các màn hồ sơ khách (Tiếp nhận, Import,
// Chi tiết khách — mỗi màn giữ danh sách riêng 0–4). Nó mô tả một tin gửi chung chứ
// không phải xưng hô của một người; gán cố định cho một khách là mọi tin của khách
// đó vĩnh viễn không được gọi tên.
export const REMINDER_TITLES = [
  { value: 0, text: 'Cô' },
  { value: 1, text: 'Chú' },
  { value: 2, text: 'Anh' },
  { value: 3, text: 'Chị' },
  { value: 4, text: 'Em' },
  { value: 5, text: 'Mọi người' },
];

export const APPROVAL_STATUS = {
  PENDING: 0,
  APPROVED: 1,
  SKIPPED: 2,
  SENT: 3,
  EXPIRED: 4,
  // Đã duyệt ở portal, tới giờ gửi thật vào nhóm Zalo -> hệ thống tự hỏi coach
  // xác nhận lần cuối trên Zalo trước khi gửi. Coach ok/sửa -> CONFIRMED (gửi
  // ngay); im lặng quá khung chờ -> EXPIRED.
  AWAITING_FINAL_CONFIRM: 5,
  CONFIRMED: 6,
  // Đã gọi Zalo và hỏng hẳn sau 3 lần thử. Tách khỏi APPROVED/CONFIRMED vì nếu
  // để nguyên thì trên màn theo dõi trông y hệt dòng "chưa tới giờ gửi".
  SEND_FAILED: 7,
};

export const APPROVAL_STATUS_TEXT = {
  0: 'Chờ duyệt',
  1: 'Đã duyệt',
  2: 'Đã bỏ qua',
  3: 'Đã gửi',
  4: 'Hết hạn',
  5: 'Đang hỏi coach xác nhận lần cuối (Zalo)',
  6: 'Đã xác nhận lần cuối',
  7: 'Gửi thất bại',
};

// Màu badge bám theo ý nghĩa: xám đậm = chưa xử lý, xanh = ổn, đỏ = mất tin nhắn.
// Không dùng 'secondary': theme này set $secondary = $gray-300 (#E4E6EF) rất nhạt,
// label-light-secondary lấy màu đó làm màu CHỮ nên gần như vô hình trên nền sáng.
export const APPROVAL_STATUS_VARIANT = {
  0: 'dark',
  1: 'success',
  2: 'warning',
  3: 'primary',
  4: 'danger',
  5: 'warning',
  6: 'success',
  7: 'danger',
};

export const REVIEW_ACTION = {
  OK: 0,
  SKIP: 1,
  EDIT: 2,
};

export function titleName(title) {
  const found = REMINDER_TITLES.find((t) => t.value === title);
  return found ? found.text : '—';
}

export default {
  // ----- Nội dung nhắc nhở + coach duyệt -----

  async generate(payload) {
    return unwrap(throwIfError(await api.post('/ReminderApproval/generate', payload)));
  },

  // { running, done, total } của lượt tạo gợi ý đang chạy; running = false là không có lượt nào
  async getGenerateProgress(userPackageId) {
    return unwrap(
      throwIfError(await api.get(`/ReminderApproval/generate-progress/${userPackageId}`)),
    );
  },

  // Một lượt gọi AI riêng để phân tích khảo sát, không ghi gì xuống DB
  async analyzeSurvey(payload) {
    return unwrap(
      throwIfError(await api.post('/ReminderApproval/analyze-survey', payload)),
    );
  },

  // Ngữ cảnh đã suy ra sẵn: nhóm khách, coach, prompt và văn phong sẽ dùng
  // title = null -> lấy theo hồ sơ khách; truyền vào để xem trước khi đổi nhóm
  async getContext(userPackageId, title = null) {
    const params = title === null || title === undefined ? {} : { title };
    return unwrap(
      throwIfError(
        await api.get(`/ReminderApproval/context/${userPackageId}`, { params }),
      ),
    );
  },

  // Coach chọn cách duyệt ngay ở bước Gọi AI (0=duyệt từng tin, 1=duyệt trước nội
  // dung) — ghi thẳng lên UserPackage, ResolveForSend đọc từ đây lúc gửi.
  async setApprovalMode(payload) {
    return unwrap(
      throwIfError(await api.put('/ReminderApproval/approval-mode', payload)),
    );
  },

  // Nhắc nhở đã đặt lịch, kèm nội dung gốc + giờ gửi + đánh dấu trùng
  async getPlanned(userPackageId) {
    return unwrap(
      throwIfError(await api.get(`/ReminderApproval/planned/${userPackageId}`)),
    );
  },

  // Mở màn sửa: ghi cây hoạt động xuống DB rồi trả danh sách đã có id, để sửa được ngay.
  // Phải PUT/POST chứ không GET vì nó có ghi.
  async preparePlanned(userPackageId) {
    return unwrap(
      throwIfError(
        await api.post(`/ReminderApproval/planned/${userPackageId}/prepare`, {}),
      ),
    );
  },

  async getPackageActivities(userPackageId) {
    return unwrap(
      throwIfError(await api.get(`/ReminderApproval/activities/${userPackageId}`)),
    );
  },

  async addPlanned(payload) {
    return unwrap(throwIfError(await api.post('/ReminderApproval/planned', payload)));
  },

  async updatePlanned(payload) {
    return unwrap(throwIfError(await api.put('/ReminderApproval/planned', payload)));
  },

  async deletePlanned(id) {
    return unwrap(throwIfError(await api.delete(`/ReminderApproval/planned/${id}`)));
  },

  // Sửa nhiều dòng cùng lúc, MỘT lần gọi — payload: { userPackageId, items: [{id, content, sendAt}] }
  async bulkUpdatePlanned(payload) {
    return unwrap(throwIfError(await api.put('/ReminderApproval/planned/bulk', payload)));
  },

  // payload: { userPackageId, ids: [...] }
  async bulkDeletePlanned(payload) {
    return unwrap(
      throwIfError(await api.post('/ReminderApproval/planned/bulk-delete', payload)),
    );
  },

  // Xoá nháp gợi ý trên Redis. Mục coach đã duyệt/bỏ qua đã xuống DB nên không mất.
  async discardDraft(userPackageId) {
    return unwrap(
      throwIfError(await api.delete(`/ReminderApproval/draft/${userPackageId}`)),
    );
  },

  async getByPackage(userPackageId) {
    return unwrap(
      throwIfError(await api.get(`/ReminderApproval/by-package/${userPackageId}`)),
    );
  },

  // Tra mot nhac nho theo ma RMD-xxx. Man theo doi dung de lay day du 4 lop
  // noi dung cho panel chi tiet - danh sach lich gui chi tra ban trich.
  async getByCode(code) {
    return unwrap(
      throwIfError(await api.get(`/ReminderApproval/by-code/${code}`)),
    );
  },

  async getQueue(params) {
    return unwrap(throwIfError(await api.get('/ReminderApproval', { params })));
  },

  // payload.forceConfirm = true -> chốt luôn, bỏ qua vòng hỏi lại qua Zalo.
  // Màn theo dõi dùng cờ này; bảng duyệt ở tab AI thì không.
  async review(payload) {
    return unwrap(throwIfError(await api.put('/ReminderApproval/review', payload)));
  },

  // Prompt của MỌI nhóm xưng hô trong một lượt. Nạp một lần lúc mở màn, sau đó đổi
  // nhóm là đổi tại chỗ — khỏi gọi lại getContext (hàm đó kéo theo chỉ số, khảo sát
  // và đếm nhắc nhở nên rất chậm).
  // Ghi chú coach cho AI chat của khách (lưu trong hồ sơ đối tượng)
  async getCoachNote(userPackageId) {
    return unwrap(throwIfError(await api.get(`/ReminderApproval/coach-note/${userPackageId}`)));
  },

  async saveCoachNote(payload) {
    return unwrap(throwIfError(await api.put('/ReminderApproval/coach-note', payload)));
  },

  async getTitlePrompts() {
    return unwrap(throwIfError(await api.get('/ReminderApproval/title-prompts')));
  },

  // Chỉ ghi nội dung coach sửa, KHÔNG duyệt và không đặt job — khác hẳn review().
  async saveContent(payload) {
    return unwrap(throwIfError(await api.put('/ReminderApproval/content', payload)));
  },

  // Khung giờ sinh hoạt của khách + cách xử lý tin rơi ngoài khung.
  // Chỉ LƯU lựa chọn — việc dời/xoá do màn hình tính rồi ghi qua planned/bulk,
  // để coach nhìn bảng trước/sau rồi mới bấm đồng ý.
  async setActivePeriods(payload) {
    return unwrap(
      throwIfError(await api.put('/ReminderApproval/active-periods', payload)),
    );
  },

  // Đặt lại lịch gửi cho nhắc nhở đã duyệt mà chưa có job (nhãn "Kẹt" trên màn
  // theo dõi). Dựng lại job cho đúng giờ đã hẹn, KHÔNG gửi ngay.
  // sendAt = mốc unix GIÂY, bỏ trống thì giữ nguyên giờ cũ. Dòng kẹt phần lớn là
  // dòng đã quá giờ, giữ giờ cũ thì không đặt lại được gì.
  async rescheduleSendJob(id, sendAt = null) {
    return unwrap(
      throwIfError(
        await api.post(`/ReminderApproval/reschedule/${id}`, { sendAt }),
      ),
    );
  },

  // Lịch gửi theo khung giờ cho màn theo dõi của coach.
  // from/to là unix GIÂY, UTC — reminder_datetime bên DB lưu UTC nên truyền giờ
  // địa phương vào là lệch 7 tiếng.
  async getSchedule(params) {
    return unwrap(
      throwIfError(await api.get('/ReminderApproval/schedule', { params })),
    );
  },

  // Nhờ AI chọn giờ trong ngày cho một loạt nhắc nhở. Trả về "HH:mm", KHÔNG có
  // ngày — chỗ gọi tự ghép ngày, nên AI không có đường nào đổi ngày.
  async suggestTimes(payload) {
    return unwrap(
      throwIfError(await api.post('/ReminderApproval/suggest-times', payload)),
    );
  },

  async changeTime(payload) {
    return unwrap(throwIfError(await api.put('/ReminderApproval/change-time', payload)));
  },
};
